/**
 * URL validation utilities for secure URL handling
 *
 * This module provides reusable functions for validating URLs, particularly
 * for Office viewer URLs and secure HTTPS validation.
 *
 * Security features:
 * - Strict hostname validation with normalization (lowercase, exact match)
 * - Protocol validation (HTTPS only for sensitive operations)
 * - Prevents bypasses like: trusted.com.evil, TRUSTED.COM, user@trusted.com@evil
 * - Safe URL parsing with error handling
 */

/**
 * Validates if a URL is a valid Office viewer URL
 *
 * Requirements:
 * - Must use HTTPS protocol
 * - Hostname must be exactly "view.officeapps.live.com" (case-insensitive)
 * - Hostname must exist and not be empty
 *
 * @param url - The URL string to validate
 * @returns true if the URL is a valid Office viewer URL, false otherwise
 *
 * @example
 * ```ts
 * isValidOfficeViewerUrl("https://view.officeapps.live.com/op/embed.aspx?src=...")
 * // => true
 *
 * isValidOfficeViewerUrl("http://view.officeapps.live.com/op/embed.aspx?src=...")
 * // => false (HTTP not allowed)
 *
 * isValidOfficeViewerUrl("https://view.officeapps.live.com.evil.com/op/embed.aspx")
 * // => false (subdomain attack)
 * ```
 */
export function isValidOfficeViewerUrl(url: string): boolean {
  try {
    const parsedUrl = new URL(url);

    // Strict hostname validation with normalization (lowercase, exact match)
    // Prevents bypasses like: trusted.com.evil, TRUSTED.COM, user@trusted.com@evil
    const normalizedHostname = parsedUrl.hostname.toLowerCase();

    return (
      normalizedHostname === "view.officeapps.live.com" &&
      parsedUrl.protocol === "https:" &&
      !!parsedUrl.hostname // Ensure hostname exists and is not empty
    );
  } catch {
    return false;
  }
}

/**
 * Validates if a URL has a valid HTTPS protocol and hostname
 *
 * Requirements:
 * - Must use HTTPS protocol
 * - Must have a non-empty hostname
 *
 * @param url - The URL string to validate
 * @returns true if the URL is valid HTTPS with hostname, false otherwise
 *
 * @example
 * ```ts
 * isValidHttpsUrl("https://example.com/file.pdf")
 * // => true
 *
 * isValidHttpsUrl("http://example.com/file.pdf")
 * // => false (HTTP not allowed)
 *
 * isValidHttpsUrl("https://")
 * // => false (no hostname)
 * ```
 */
export function isValidHttpsUrl(url: string): boolean {
  try {
    const parsedUrl = new URL(url);

    // Only allow HTTPS protocol and validate it's not malformed
    return parsedUrl.protocol === "https:" && !!parsedUrl.hostname;
  } catch {
    return false;
  }
}

/**
 * Extracts and validates the src parameter from an Office viewer URL
 *
 * This function:
 * 1. Validates the Office viewer URL
 * 2. Extracts the "src" query parameter
 * 3. Validates the src URL is HTTPS with a hostname
 * 4. Returns the validated src URL or null if invalid
 *
 * @param officeViewerUrl - The Office viewer URL containing the src parameter
 * @returns The validated src URL or null if invalid/missing
 *
 * @example
 * ```ts
 * const officeUrl = "https://view.officeapps.live.com/op/embed.aspx?src=https://example.com/file.xlsx";
 * extractValidSrcFromOfficeUrl(officeUrl)
 * // => "https://example.com/file.xlsx"
 *
 * const invalidUrl = "https://view.officeapps.live.com/op/embed.aspx?src=http://example.com/file.xlsx";
 * extractValidSrcFromOfficeUrl(invalidUrl)
 * // => null (src must be HTTPS)
 * ```
 */
export function extractValidSrcFromOfficeUrl(officeViewerUrl: string): string | null {
  try {
    const url = new URL(officeViewerUrl);

    // Validate this is a legitimate Office viewer URL
    if (!isValidOfficeViewerUrl(officeViewerUrl)) {
      return null;
    }

    const src = url.searchParams.get("src");

    if (!src) {
      return null;
    }

    // Validate the src parameter URL to prevent open redirect
    if (isValidHttpsUrl(src)) {
      return src;
    }

    return null;
  } catch {
    return null;
  }
}

/**
 * Extracts a safe download URL from a file URL
 *
 * This function handles two scenarios:
 * 1. Office viewer URLs: Extracts and validates the src parameter
 * 2. Regular URLs: Returns the URL as-is
 *
 * @param fileUrl - The file URL (could be Office viewer or regular URL)
 * @returns The safe download URL, or undefined if parsing fails
 *
 * @example
 * ```ts
 * // Office viewer URL
 * const officeUrl = "https://view.officeapps.live.com/op/embed.aspx?src=https://example.com/file.xlsx";
 * extractSafeDownloadUrl(officeUrl)
 * // => "https://example.com/file.xlsx"
 *
 * // Regular URL
 * extractSafeDownloadUrl("https://example.com/document.pdf")
 * // => "https://example.com/document.pdf"
 *
 * // Invalid URL
 * extractSafeDownloadUrl("not a url")
 * // => undefined
 * ```
 */
export function extractSafeDownloadUrl(fileUrl: string | null | undefined): string | undefined {
  if (!fileUrl) {
    return undefined;
  }

  try {
    // If it's an Office viewer URL, extract the src parameter for the original file URL
    const validSrc = extractValidSrcFromOfficeUrl(fileUrl);

    if (validSrc) {
      return validSrc;
    }

    // For non-Office viewer URLs, validate they can be parsed
    const parsedUrl = new URL(fileUrl);

    // Only allow http: and https: protocols for download URLs
    // Reject dangerous protocols like javascript:, data:, blob:, etc.
    if (parsedUrl.protocol !== "http:" && parsedUrl.protocol !== "https:") {
      return undefined;
    }

    return fileUrl;
  } catch {
    // If parsing fails, return undefined for safety
    return undefined;
  }
}
